import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  Plus, 
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Upload,
  Eye,
  Search,
  Filter
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Import the existing components to embed them in tabs
import DocumentRequests from './DocumentRequests';
import ParentDocuments from './ParentDocuments';
import FormTemplates from './FormTemplates';

interface DashboardStats {
  activeRequests: number;
  pendingDocuments: number;
  completedRequests: number;
  availableTemplates: number;
  overdueRequests: number;
  thisMonthEnvelopes: number;
  envelopeLimit: number;
}

export default function DocumentManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<DashboardStats>({
    activeRequests: 0,
    pendingDocuments: 0,
    completedRequests: 0,
    availableTemplates: 0,
    overdueRequests: 0,
    thisMonthEnvelopes: 0,
    envelopeLimit: 5 // Default to essentials tier
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, [user]);

  const getEnvelopeLimitFromSettings = (quotaSettings: any, tier: string): number => {
    const defaultQuotas = {
      "trial": 0,
      "essentials": 5,
      "advanced": 50,
      "enterprise": 100,
      "paid_active": 5,
      "trial_active": 0
    };
    
    if (quotaSettings && typeof quotaSettings === 'object') {
      return quotaSettings[tier] || defaultQuotas[tier] || 0;
    }
    
    return defaultQuotas[tier] || 0;
  };

  const loadDashboardStats = async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      // Optimize all queries with Promise.all
      const [
        { count: activeRequests },
        { count: pendingDocs },
        { count: completedRequests },
        { count: templates },
        { count: overdueRequests },
        { data: envelopeQuota }
      ] = await Promise.all([
        supabase
          .from('document_requests')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', user.organization_id)
          .eq('status', 'active'),
        
        supabase
          .from('document_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', user.organization_id)
          .eq('status', 'pending'),
        
        supabase
          .from('document_requests')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', user.organization_id)
          .eq('status', 'completed'),
        
        supabase
          .from('form_templates')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', user.organization_id)
          .eq('status', 'active'),
        
        supabase
          .from('document_requests')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', user.organization_id)
          .eq('status', 'active')
          .lt('due_date', new Date().toISOString()),

        supabase.rpc('check_envelope_quota', { org_id: user.organization_id })
      ]);

      setStats({
        activeRequests: activeRequests || 0,
        pendingDocuments: pendingDocs || 0,
        completedRequests: completedRequests || 0,
        availableTemplates: templates || 0,
        overdueRequests: overdueRequests || 0,
        thisMonthEnvelopes: envelopeQuota?.[0]?.envelopes_used || 0,
        envelopeLimit: envelopeQuota?.[0]?.envelopes_limit || 5
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'text-blue-600', bgColor = 'bg-blue-50' }: {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color?: string;
    bgColor?: string;
  }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{loading ? '-' : value}</p>
          </div>
          <div className={`p-3 rounded-full ${bgColor}`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const QuickActions = () => (
    <Card>
      <CardHeader>
        <CardTitle>Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button 
          className="w-full justify-start" 
          variant="outline"
          onClick={() => setActiveTab('requests')}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Request
        </Button>
        <Button 
          className="w-full justify-start" 
          variant="outline"
          onClick={() => setActiveTab('templates')}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Template
        </Button>
        <Button 
          className="w-full justify-start" 
          variant="outline"
          onClick={() => setActiveTab('documents')}
        >
          <Eye className="h-4 w-4 mr-2" />
          Review Documents
        </Button>
      </CardContent>
    </Card>
  );

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard 
          title="Active Requests" 
          value={stats.activeRequests} 
          icon={FileText}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatCard 
          title="Pending Documents" 
          value={stats.pendingDocuments} 
          icon={Clock}
          color="text-yellow-600"
          bgColor="bg-yellow-50"
        />
        <StatCard 
          title="Completed Requests" 
          value={stats.completedRequests} 
          icon={CheckCircle}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard 
          title="Available Templates" 
          value={stats.availableTemplates} 
          icon={BarChart3}
          color="text-purple-600"
          bgColor="bg-purple-50"
        />
        <StatCard 
          title="Overdue Requests" 
          value={stats.overdueRequests} 
          icon={AlertCircle}
          color="text-red-600"
          bgColor="bg-red-50"
        />
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Electronic Envelopes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? '-' : `${stats.thisMonthEnvelopes}/${stats.envelopeLimit}`}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-50">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            {!loading && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      stats.thisMonthEnvelopes / stats.envelopeLimit > 0.8 
                        ? 'bg-red-500' 
                        : stats.thisMonthEnvelopes / stats.envelopeLimit > 0.6
                        ? 'bg-yellow-500'
                        : 'bg-purple-500'
                    }`}
                    style={{ 
                      width: `${Math.min((stats.thisMonthEnvelopes / stats.envelopeLimit) * 100, 100)}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.envelopeLimit - stats.thisMonthEnvelopes} remaining this billing period
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <QuickActions />
        
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Quick Navigation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center gap-2"
                onClick={() => setActiveTab('requests')}
              >
                <Plus className="h-6 w-6" />
                <span className="text-sm">Create Request</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center gap-2"
                onClick={() => setActiveTab('documents')}
              >
                <Eye className="h-6 w-6" />
                <span className="text-sm">Review Documents</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center gap-2"
                onClick={() => setActiveTab('templates')}
              >
                <Upload className="h-6 w-6" />
                <span className="text-sm">Manage Templates</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center gap-2"
                onClick={() => window.open('/admin/help', '_blank')}
              >
                <Search className="h-6 w-6" />
                <span className="text-sm">Get Help</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Management</h1>
          <p className="text-gray-600">Manage document requests, submissions, and templates</p>
        </div>
        <Button onClick={() => setActiveTab('requests')}>
          <Plus className="h-4 w-4 mr-2" />
          Create Request
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="requests">Active Requests</TabsTrigger>
          <TabsTrigger value="documents">Submitted Documents</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab />
        </TabsContent>
        
        <TabsContent value="requests" className="space-y-4">
          <DocumentRequests />
        </TabsContent>
        
        <TabsContent value="documents" className="space-y-4">
          <ParentDocuments />
        </TabsContent>
        
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Quick Template Actions */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start" 
                    onClick={() => setActiveTab('requests')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Electronic Request
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => window.open('/admin/form-templates', '_blank')}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload New Template
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => window.open('/admin/help', '_blank')}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Template Help
                  </Button>
                </CardContent>
              </Card>

              {/* Template Stats */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Template Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Available Templates</span>
                      <span className="font-semibold">{stats.availableTemplates}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Used This Month</span>
                      <span className="font-semibold">{stats.thisMonthEnvelopes}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Remaining Envelopes</span>
                      <span className={`font-semibold ${
                        stats.envelopeLimit - stats.thisMonthEnvelopes <= 1 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {stats.envelopeLimit - stats.thisMonthEnvelopes}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Full Template Management */}
            <div className="lg:col-span-3">
              <FormTemplates />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}